/**
 * The workpiece.
 *
 * One contract for what a person is holding as they move between Home, a
 * finding, Ask, Explore, an agent, a run, an approval, and the library.
 * Changing surface must feel like changing instrument, not starting over.
 *
 * Three rules this module exists to enforce:
 *
 *  1. **Identity is structured, never prose.** Subjects carry real `{id, kind}`
 *     pairs. A name in a sentence cannot be re-resolved, joined, or scoped
 *     against; an id can.
 *  2. **Context never grants authorization.** Everything here is descriptive —
 *     it records what someone was looking at. Every surface that resolves an id
 *     re-authorizes it. A hand-crafted context can name ids its bearer still
 *     cannot read, and must simply come back empty.
 *  3. **Nothing is silently replaced.** Changing the function lens re-frames
 *     the same subjects; it never swaps them out. Anything dropped for a limit
 *     is recorded so the UI can say so.
 */

// v2 adds the Decision Desk Ask identifiers (agentId,
// appliedVersion, route) alongside the existing subjects/evidence/origin
// contract. A v1 payload lacking them decodes cleanly with those fields
// undefined — this is an additive migration, not a breaking one.
//
// explorationId and recordIds (added for canonical Explore, HOW_SIXDEGREE_WORKS.md's
// "Explore is one investigation with three projections") are the same kind of
// addition: a payload from before they existed decodes with explorationId
// undefined and recordIds empty, so no further version bump was needed.
export const CONTEXT_VERSION = 2;

export type FunctionLens = 'revenue' | 'support' | 'cross-functional';

const LENSES: FunctionLens[] = ['revenue', 'support', 'cross-functional'];

/** What a handoff came from, so there is always a way back. */
type OriginKind =
  | 'follow-up'
  | 'entity'
  | 'cohort'
  | 'chat'
  | 'run'
  | 'approval'
  | 'report'
  | 'answer'
  | 'view'
  // The global Decision Desk Ask bar (Cmd+K), as opposed to a question seeded
  // from a finding, an entity, or a follow-up opened from an Exploration.
  // This is the one signal the Ask → Explore handoff (lib/explore/exploration-handoff.ts)
  // uses to decide whether a successful `create_exploration` result should
  // navigate the user there automatically.
  | 'ask-bar';

const ORIGIN_KINDS: OriginKind[] = [
  'follow-up',
  'entity',
  'cohort',
  'chat',
  'run',
  'approval',
  'report',
  'answer',
  'view',
  'ask-bar',
];

export interface ContextSubject {
  id: string;
  kind: string;
  name: string;
}

export interface ContextCohort {
  /** Saved view or stored context id, when the cohort is durable. */
  id?: string;
  label: string;
  /** How many members the cohort actually had, before any clamping. */
  size?: number;
  /** True when `subjects` is a clamped view of a larger set. */
  truncated?: boolean;
}

export interface ContextFilter {
  key: string;
  value: string;
  label?: string;
}

export interface ContextTimeScope {
  /** Bitemporal read instant, matching Explore's `as_of`. */
  asOf?: string;
  since?: string;
  label?: string;
}

/**
 * A pointer to evidence — never the evidence itself. Verdicts stay owned by
 * the deterministic computes that produced them; carrying a copy would let a
 * stale snapshot argue with the ledger.
 */
export interface EvidenceRef {
  followUpId?: string;
  claim?: string;
  sourceSystem?: string;
  sourceId?: string;
  observedAt?: string;
  freshness?: string;
}

export interface ContextOrigin {
  label: string;
  href?: string;
  kind?: OriginKind;
  id?: string;
  /** Legacy alias retained so existing finding handoffs keep working. */
  followUpId?: string;
}

export interface ContextReturnPath {
  label: string;
  href: string;
}

export interface WorkingContext {
  v: number;
  lens?: FunctionLens;
  subjects: ContextSubject[];
  cohort?: ContextCohort;
  filters: ContextFilter[];
  timeScope?: ContextTimeScope;
  evidence: EvidenceRef[];
  origin?: ContextOrigin;
  /** What the person is trying to do, in their words. */
  intent?: string;
  returnPath?: ContextReturnPath;
  /**
   * Decision Desk Ask scope. Ask is contextual chrome, never a required
   * field: all four travel only when the header actually had them to give.
   */
  agentId?: string;
  /** The decision/policy version applied when Ask was opened. */
  appliedVersion?: string;
  /**
   * Safe internal path Ask was opened from. Distinct from `returnPath`: this
   * is where the conversation itself was invoked, `returnPath` is where
   * "back" goes, and the two can diverge (e.g. opened from a Review, headed
   * back to Home).
   */
  route?: string;
  /**
   * The Exploration this workpiece was carried out of, when Ask or a
   * follow-up was opened from Explore. A pointer, not a copy: the
   * Exploration's own pinned query and evidence coordinates stay
   * server-owned, this just says which one a question is continuing from.
   */
  explorationId?: string;
  /**
   * Ids of records selected in the Exploration's Records projection when the
   * handoff happened, before they were necessarily resolved into named
   * `subjects`. Same cap as `subjects` (`LIMITS.subjects`) — this is the same
   * kind of collection, just earlier in its life.
   */
  recordIds: string[];
}

/** Bounds. The server's 64 KiB payload cap sits well above what these allow. */
export const LIMITS = {
  subjects: 50,
  filters: 20,
  evidence: 50,
  text: 200,
  intent: 500,
} as const;

export const EMPTY_WORKING_CONTEXT: WorkingContext = {
  v: CONTEXT_VERSION,
  subjects: [],
  filters: [],
  evidence: [],
  recordIds: [],
};

function str(value: unknown, max: number = LIMITS.text): string {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, max);
}

function optStr(value: unknown, max: number = LIMITS.text): string | undefined {
  const s = str(value, max);
  return s || undefined;
}

/**
 * Only same-origin application paths may be stored as somewhere to navigate.
 * A context is user-supplied data that later becomes an href; without this a
 * shared link is an open redirect, and `javascript:` is a script gadget.
 */
export function safeInternalHref(value: unknown): string | undefined {
  const raw = str(value, 2048);
  if (!raw.startsWith('/')) return undefined;
  // `//host` and `/\host` are protocol-relative — they leave the origin.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return undefined;
  // Control characters (newlines, NUL) can smuggle a second value past naive
  // parsers downstream. A legitimate application path never contains one.
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return undefined;
  }
  return raw;
}

function sanitizeSubjects(value: unknown): {
  subjects: ContextSubject[];
  dropped: number;
} {
  if (!Array.isArray(value)) return { subjects: [], dropped: 0 };
  const seen = new Set<string>();
  const all: ContextSubject[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const record = raw as Record<string, unknown>;
    const subject = {
      id: str(record.id),
      kind: str(record.kind),
      name: str(record.name),
    };
    // A subject without a real id is prose wearing a chip. Drop it rather than
    // carry something that cannot be re-resolved on the other side.
    if (!subject.id || !subject.kind || !subject.name) continue;
    const key = `${subject.kind}:${subject.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    all.push(subject);
  }
  return {
    subjects: all.slice(0, LIMITS.subjects),
    dropped: Math.max(0, all.length - LIMITS.subjects),
  };
}

function sanitizeFilters(value: unknown): ContextFilter[] {
  if (!Array.isArray(value)) return [];
  const out: ContextFilter[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const record = raw as Record<string, unknown>;
    const key = str(record.key);
    const filterValue = str(record.value);
    if (!key || !filterValue) continue;
    out.push({
      key,
      value: filterValue,
      ...(optStr(record.label) ? { label: str(record.label) } : {}),
    });
    if (out.length >= LIMITS.filters) break;
  }
  return out;
}

/**
 * Ids only — no kind/name requirement, unlike `sanitizeSubjects`. A record
 * selected in Explore's Records projection may not have a resolved display
 * name yet; the id alone is enough to re-authorize and re-resolve it on the
 * receiving end.
 */
function sanitizeRecordIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value) {
    const id = str(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= LIMITS.subjects) break;
  }
  return out;
}

function sanitizeEvidence(value: unknown): EvidenceRef[] {
  if (!Array.isArray(value)) return [];
  const out: EvidenceRef[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const record = raw as Record<string, unknown>;
    const ref: EvidenceRef = {
      ...(optStr(record.followUpId) ? { followUpId: str(record.followUpId) } : {}),
      ...(optStr(record.claim) ? { claim: str(record.claim) } : {}),
      ...(optStr(record.sourceSystem)
        ? { sourceSystem: str(record.sourceSystem) }
        : {}),
      ...(optStr(record.sourceId) ? { sourceId: str(record.sourceId) } : {}),
      ...(optStr(record.observedAt)
        ? { observedAt: str(record.observedAt) }
        : {}),
      ...(optStr(record.freshness) ? { freshness: str(record.freshness) } : {}),
    };
    if (Object.keys(ref).length === 0) continue;
    out.push(ref);
    if (out.length >= LIMITS.evidence) break;
  }
  return out;
}

function sanitizeOrigin(value: unknown): ContextOrigin | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const label = str(record.label);
  if (!label) return undefined;
  const href = safeInternalHref(record.href);
  const kind = ORIGIN_KINDS.includes(record.kind as OriginKind)
    ? (record.kind as OriginKind)
    : undefined;
  return {
    label,
    ...(href ? { href } : {}),
    ...(kind ? { kind } : {}),
    ...(optStr(record.id) ? { id: str(record.id) } : {}),
    ...(optStr(record.followUpId) ? { followUpId: str(record.followUpId) } : {}),
  };
}

function sanitizeCohort(value: unknown): ContextCohort | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const label = str(record.label);
  if (!label) return undefined;
  const size =
    typeof record.size === 'number' && Number.isFinite(record.size)
      ? Math.max(0, Math.floor(record.size))
      : undefined;
  return {
    label,
    ...(optStr(record.id) ? { id: str(record.id) } : {}),
    ...(size !== undefined ? { size } : {}),
    ...(record.truncated === true ? { truncated: true } : {}),
  };
}

function sanitizeTimeScope(value: unknown): ContextTimeScope | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const scope: ContextTimeScope = {
    ...(optStr(record.asOf) ? { asOf: str(record.asOf) } : {}),
    ...(optStr(record.since) ? { since: str(record.since) } : {}),
    ...(optStr(record.label) ? { label: str(record.label) } : {}),
  };
  return Object.keys(scope).length > 0 ? scope : undefined;
}

/**
 * The trust boundary. Every context entering the app — from a stored payload,
 * a URL, another tab's storage — passes through here first. Unknown fields are
 * discarded rather than forwarded, so a payload cannot smuggle anything past
 * the contract.
 */
export function sanitizeWorkingContext(value: unknown): WorkingContext {
  if (!value || typeof value !== 'object') return { ...EMPTY_WORKING_CONTEXT };
  const raw = value as Record<string, unknown>;

  const lens = LENSES.includes(raw.lens as FunctionLens)
    ? (raw.lens as FunctionLens)
    : undefined;
  const { subjects, dropped } = sanitizeSubjects(raw.subjects);
  let cohort = sanitizeCohort(raw.cohort);
  // Clamping is a fact about the workpiece, not an implementation detail. If
  // the UI cannot say "showing 50 of 214", the user believes they are asking
  // about all of them.
  if (dropped > 0) {
    cohort = {
      label: cohort?.label ?? 'Selected records',
      ...(cohort?.id ? { id: cohort.id } : {}),
      size: cohort?.size ?? subjects.length + dropped,
      truncated: true,
    };
  }
  const returnPathHref = safeInternalHref(
    (raw.returnPath as Record<string, unknown> | undefined)?.href,
  );
  const returnPathLabel = str(
    (raw.returnPath as Record<string, unknown> | undefined)?.label,
  );
  const origin = sanitizeOrigin(raw.origin);
  const timeScope = sanitizeTimeScope(raw.timeScope);
  const intent = optStr(raw.intent, LIMITS.intent);
  const agentId = optStr(raw.agentId);
  const appliedVersion = optStr(raw.appliedVersion);
  const route = safeInternalHref(raw.route);
  const explorationId = optStr(raw.explorationId);

  return {
    v: CONTEXT_VERSION,
    ...(lens ? { lens } : {}),
    subjects,
    ...(cohort ? { cohort } : {}),
    filters: sanitizeFilters(raw.filters),
    ...(timeScope ? { timeScope } : {}),
    evidence: sanitizeEvidence(raw.evidence),
    ...(origin ? { origin } : {}),
    ...(intent ? { intent } : {}),
    ...(returnPathHref && returnPathLabel
      ? { returnPath: { label: returnPathLabel, href: returnPathHref } }
      : {}),
    ...(agentId ? { agentId } : {}),
    ...(appliedVersion ? { appliedVersion } : {}),
    ...(route ? { route } : {}),
    ...(explorationId ? { explorationId } : {}),
    recordIds: sanitizeRecordIds(raw.recordIds),
  };
}

/**
 * Serialize a context for a wire payload or storage, sanitizing first so
 * nothing invalid is ever written. Pair with `decodeWorkingContext` — every
 * write site that used to hand-roll `JSON.stringify(sanitizeWorkingContext(x))`
 * should call this instead so the two ends of a handoff cannot drift apart.
 */
export function encodeWorkingContext(context: WorkingContext): string {
  return JSON.stringify(sanitizeWorkingContext(context));
}

/**
 * The read-side counterpart of `encodeWorkingContext`. Malformed JSON, a
 * missing value, and a value that was never a context at all all come back as
 * the same empty context — a corrupt handoff must look like no handoff, never
 * an error the caller has to special-case.
 */
export function decodeWorkingContext(
  raw: string | null | undefined,
): WorkingContext {
  if (!raw) return { ...EMPTY_WORKING_CONTEXT };
  try {
    return sanitizeWorkingContext(JSON.parse(raw));
  } catch {
    return { ...EMPTY_WORKING_CONTEXT };
  }
}

export function isEmptyContext(context: WorkingContext): boolean {
  return (
    !context.lens &&
    context.subjects.length === 0 &&
    context.filters.length === 0 &&
    context.evidence.length === 0 &&
    !context.cohort &&
    !context.timeScope &&
    !context.origin &&
    !context.intent &&
    !context.agentId &&
    !context.appliedVersion &&
    !context.route &&
    !context.explorationId &&
    context.recordIds.length === 0
  );
}

/**
 * Re-frame the same workpiece under a different lens. Subjects, cohort,
 * filters, time scope, evidence and origin are all preserved — a lens is an
 * interpretation, and swapping interpretation must never swap the thing being
 * interpreted.
 */
export function withLens(
  context: WorkingContext,
  lens: FunctionLens,
): WorkingContext {
  return { ...context, lens };
}

export function withSubject(
  context: WorkingContext,
  subject: ContextSubject,
): WorkingContext {
  const key = `${subject.kind}:${subject.id}`;
  if (context.subjects.some((s) => `${s.kind}:${s.id}` === key)) return context;
  return sanitizeWorkingContext({
    ...context,
    subjects: [...context.subjects, subject],
  });
}

export function withoutSubject(
  context: WorkingContext,
  subject: Pick<ContextSubject, 'id' | 'kind'>,
): WorkingContext {
  return {
    ...context,
    subjects: context.subjects.filter(
      (s) => s.id !== subject.id || s.kind !== subject.kind,
    ),
  };
}

/**
 * An immutable record of the workpiece at one moment — one chat turn, one run.
 * Later edits to the live context must never rewrite what a past answer was
 * actually scoped to, or the transcript stops being evidence of anything.
 */
interface ContextSnapshot {
  readonly context: WorkingContext;
  readonly capturedAt: string;
}

export function captureSnapshot(
  context: WorkingContext,
  capturedAt: string,
): ContextSnapshot {
  const frozen = sanitizeWorkingContext(
    JSON.parse(JSON.stringify(context)),
  ) as WorkingContext;
  frozen.subjects.forEach((s) => Object.freeze(s));
  Object.freeze(frozen.subjects);
  Object.freeze(frozen.filters);
  Object.freeze(frozen.evidence);
  Object.freeze(frozen);
  return Object.freeze({ context: frozen, capturedAt });
}

/**
 * The prose the model sees. Ids travel alongside names so the agent can call a
 * tool with the exact record rather than re-resolving a display name — but the
 * structured context is what surfaces act on. This string is a briefing, not
 * the scope contract.
 */
export function contextSystemMessage(context: WorkingContext): string | null {
  if (isEmptyContext(context)) return null;
  const lines = [
    'Working context supplied explicitly by the SixDegree interface:',
    `- Function lens: ${context.lens ?? 'cross-functional'}`,
  ];
  if (context.intent) lines.push(`- User intent: ${context.intent}`);
  if (context.subjects.length > 0) {
    lines.push(
      `- Scoped subjects: ${context.subjects
        .map((subject) => `${subject.name} (${subject.kind}:${subject.id})`)
        .join(', ')}`,
    );
  }
  if (context.cohort) {
    lines.push(
      `- Cohort: ${context.cohort.label}${
        context.cohort.truncated
          ? ` (showing ${context.subjects.length} of ${context.cohort.size ?? 'many'} — say so if the answer depends on the rest)`
          : ''
      }`,
    );
  }
  if (context.filters.length > 0) {
    lines.push(
      `- Filters: ${context.filters
        .map((f) => `${f.label ?? f.key}=${f.value}`)
        .join(', ')}`,
    );
  }
  if (context.timeScope?.asOf) {
    lines.push(`- Reading the graph as of: ${context.timeScope.asOf}`);
  }
  if (context.evidence.length > 0) {
    lines.push(
      `- Evidence already shown to the user: ${context.evidence
        .map((e) =>
          [e.claim, e.sourceSystem, e.observedAt].filter(Boolean).join(' · '),
        )
        .filter(Boolean)
        .join('; ')}`,
    );
  }
  if (context.origin) {
    lines.push(`- Origin: ${context.origin.label}`);
    if (context.origin.kind) {
      lines.push(`- Origin kind: ${context.origin.kind}`);
    }
  }
  if (context.explorationId) {
    lines.push(`- Continuing from Exploration: ${context.explorationId}`);
  }
  if (context.recordIds.length > 0) {
    lines.push(
      `- Records selected in Explore: ${context.recordIds.join(', ')}`,
    );
  }
  if (context.agentId) lines.push(`- Scoped to Agent: ${context.agentId}`);
  if (context.appliedVersion) {
    lines.push(`- Applied decision/policy version: ${context.appliedVersion}`);
  }
  lines.push(
    'Interpret and prioritize through the function lens, but inspect relevant evidence across functions. The lens does not grant access or override evidence. State when cross-functional evidence materially affects the answer.',
  );
  // The scope contract is only trustworthy if its effect is visible. Opening
  // with what was actually covered is how the user learns the context line
  // above the composer is real - and how a clamped cohort stays honest.
  lines.push(
    'Open your answer with one short clause naming what it covers (e.g. "Across the 12 accounts you brought from Explore, ..."). If the cohort was truncated, say you answered about the subset. Skip the clause only when no subjects or cohort are in scope.',
  );
  return lines.join('\n');
}
